/*
The MIT License (MIT)


Copyright (c) 2013 Mikola Lysenko


Permission is hereby granted, free of charge, to any person obtaining a copy

of this software and associated documentation files (the "Software"), to deal

in the Software without restriction, including without limitation the rights

to use, copy, modify, merge, publish, distribute, sublicense, and/or sell

copies of the Software, and to permit persons to whom the Software is

furnished to do so, subject to the following conditions:


The above copyright notice and this permission notice shall be included in

all copies or substantial portions of the Software.


THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR

IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,

FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE

AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER

LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,

OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN

THE SOFTWARE.
*/
/**
 * Returns a pair of 32-bit unsigned ints encoding the lower/higher order words respectively representing n
 * @param n an IEEE754 double number
 * @returns An array, [lo,hi] encoding n
 */
export let unpack;
/**
 * Returns the higher order word of n
 * @param n an IEEE754 double number
 * @returns The higher order word of n
 */
export let hi;
/**
 * Returns the lower order word of n
 * @param n an IEEE754 double number
 * @returnsThe lower order word of n
 */
export let lo;
/**
 * Given a pair of lower/higher order words, concatenate them into a 64 bit double precision number
 * @param lo the lower order word
 * @param hi the higher order word
 * @returns An IEEE754 double precision number formed by concatenating the bits of lo and hi
 */
export let pack;
let hasTypedArrays = false;
if (typeof Float64Array !== "undefined") {
    const DOUBLE_VIEW = new Float64Array(1);
    const UINT_VIEW = new Uint32Array(DOUBLE_VIEW.buffer);
    DOUBLE_VIEW[0] = 1.0;
    hasTypedArrays = true;
    if (UINT_VIEW[1] === 0x3ff00000) {
        //Use little endian
        const doubleBitsLE = (n) => {
            DOUBLE_VIEW[0] = n;
            return [UINT_VIEW[0], UINT_VIEW[1]];
        };
        unpack = doubleBitsLE;
        const toDoubleLE = (lo, hi) => {
            UINT_VIEW[0] = lo;
            UINT_VIEW[1] = hi;
            return DOUBLE_VIEW[0];
        };
        pack = toDoubleLE;
        const lowUintLE = (n) => {
            DOUBLE_VIEW[0] = n;
            return UINT_VIEW[0];
        };
        lo = lowUintLE;
        const highUintLE = (n) => {
            DOUBLE_VIEW[0] = n;
            return UINT_VIEW[1];
        };
        hi = highUintLE;
    }
    else if (UINT_VIEW[0] === 0x3ff00000) {
        //Use big endian
        const doubleBitsBE = (n) => {
            DOUBLE_VIEW[0] = n;
            return [UINT_VIEW[1], UINT_VIEW[0]];
        };
        unpack = doubleBitsBE;
        const toDoubleBE = (lo, hi) => {
            UINT_VIEW[1] = lo;
            UINT_VIEW[0] = hi;
            return DOUBLE_VIEW[0];
        };
        pack = toDoubleBE;
        const lowUintBE = (n) => {
            DOUBLE_VIEW[0] = n;
            return UINT_VIEW[1];
        };
        lo = lowUintBE;
        const highUintBE = (n) => {
            DOUBLE_VIEW[0] = n;
            return UINT_VIEW[0];
        };
        hi = highUintBE;
    }
    else {
        hasTypedArrays = false;
    }
}
if (!hasTypedArrays) {
    var buffer = Buffer.alloc(8);
    const doubleBits = (n) => {
        buffer.writeDoubleLE(n, 0); //, true)
        return [
            buffer.readUInt32LE(0), //, true),
            buffer.readUInt32LE(4), //, true)
        ];
    };
    unpack = doubleBits;
    const toDouble = (lo, hi) => {
        buffer.writeUInt32LE(lo, 0); //, true)
        buffer.writeUInt32LE(hi, 4); //, true)
        return buffer.readDoubleLE(0); //, true)
    };
    pack = toDouble;
    const lowUint = (n) => {
        buffer.writeDoubleLE(n, 0); //, true)
        return buffer.readUInt32LE(0); //, true)
    };
    lo = lowUint;
    const highUint = (n) => {
        buffer.writeDoubleLE(n, 0); //, true)
        return buffer.readUInt32LE(4); //, true)
    };
    hi = highUint;
}
/**
 * Returns the state of the sign bit of n
 * @param n an IEEE754 double number
 * @returns The sign bit of n
 */
export const sign = (n) => {
    return hi(n) >>> 31;
};
/**
 * Returns the exponent of n
 * @param n an IEEE754 double number
 * @returns The exponent of n
 */
export const exponent = (n) => {
    const b = hi(n);
    return ((b << 1) >>> 21) - 1023;
};
/**
 * Returns the fractional part of n
 * @param n an IEEE754 double number
 * @returns The fractional part of n encoded
 * as a pair of numbers, [lo,hi] where lo is a
 * 32 bit integer and hi is a 21 bit integer.
 */
export const fraction = (n) => {
    const _lo = lo(n);
    const _hi = hi(n);
    let b = _hi & ((1 << 20) - 1);
    if (_hi & 0x7ff00000) {
        b += 1 << 20;
    }
    return [_lo, b];
};
/**
 * Test if a double is denormalized.
 * @param n a number
 * @returns true if n is denormal, false otherwise
 */
export const denormalized = (n) => {
    const _hi = hi(n);
    return !(_hi & 0x7ff00000);
};
/*
The MIT License (MIT)


Copyright (c) 2016 The Compute.io Authors.


Permission is hereby granted, free of charge, to any person obtaining a copy

of this software and associated documentation files (the "Software"), to deal

in the Software without restriction, including without limitation the rights

to use, copy, modify, merge, publish, distribute, sublicense, and/or sell

copies of the Software, and to permit persons to whom the Software is

furnished to do so, subject to the following conditions:


The above copyright notice and this permission notice shall be included in all

copies or substantial portions of the Software.


THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR

IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,

FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE

AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER

LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,

OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE

SOFTWARE.

*/
const SMALLEST_DENORM = Math.pow(2, -1074);
const UINT_MAX = -1 >>> 0;
/**
 * Returns the next representable double-precision floating-point number after x toward y.
 * @param x
 * @param y
 * @returns
 */
export const nextAfter = (x, y) => {
    if (isNaN(x) || isNaN(y)) {
        return NaN;
    }
    if (x === y) {
        return x;
    }
    if (x === 0) {
        if (y < 0) {
            return -SMALLEST_DENORM;
        }
        else {
            return SMALLEST_DENORM;
        }
    }
    let _hi = hi(x);
    let _lo = lo(x);
    if (y > x === x > 0) {
        if (_lo === UINT_MAX) {
            _hi += 1;
            _lo = 0;
        }
        else {
            _lo += 1;
        }
    }
    else {
        if (_lo === 0) {
            _lo = UINT_MAX;
            _hi -= 1;
        }
        else {
            _lo -= 1;
        }
    }
    return pack(_lo, _hi);
};
